import { getMomentTz } from "@dexteel/mesf-core";
import {
  ColDef,
  ColGroupDef,
  exportMultipleSheetsAsExcel,
} from "ag-grid-enterprise";
import { ExportGridRef } from "../../models/ProcessByShift";

const moment = getMomentTz();

/**
 * Exports a single AG-Grid to an Excel file with custom formatting and metadata.
 *
 * This function takes an AG-Grid reference and production date, processes the grid data,
 * filters out unwanted columns (like "Manual" columns), and exports the data to an Excel
 * file with custom headers and footers.
 *
 * @param grid - The grid reference containing the AG-Grid API and sheet name
 * @param grid.gridApi - AG-Grid API instance for accessing grid data and column definitions
 * @param grid.sheetName - Name to be used for the Excel sheet and file naming
 * @param productionDate - The production date to include in the report header and filename
 *
 * @throws {Error} Throws an error if the export process fails
 *
 * @example
 * ```typescript
 * const gridRef: ExportGridRef = {
 *   gridApi: gridApiInstance,
 *   sheetName: "EAF Production Report"
 * };
 * const date = new Date('2025-01-15');
 * exportSingleGridToExcel(gridRef, date);
 * ```
 */
export const exportSingleGridToExcel = (
  grid: ExportGridRef,
  productionDate: Date,
) => {
  try {
    const { gridApi, sheetName } = grid;
    const columnDefs = gridApi.getColumnDefs();

    const filterColumns = (
      columns: (ColDef | ColGroupDef)[],
    ): (ColDef | ColGroupDef)[] => {
      return columns.reduce(
        (filteredColumns: (ColDef | ColGroupDef)[], column) => {
          if (column.headerName === "" || column.headerName === undefined) {
            return filteredColumns;
          }

          if ((column as ColGroupDef).children) {
            const group = column as ColGroupDef;
            group.children = filterColumns(group.children);
            if (group.children.length > 0) {
              filteredColumns.push(group);
            }
          } else {
            filteredColumns.push(column);
          }
          return filteredColumns;
        },
        [],
      );
    };

    const filteredColumns = columnDefs ? filterColumns(columnDefs) : [];

    const columnKeys = filteredColumns
      .flatMap((column) => {
        if ((column as ColGroupDef).children) {
          return (column as ColGroupDef).children.map(
            (child) => (child as ColDef).field,
          );
        } else {
          return (column as ColDef).field;
        }
      })
      .filter((key) => key !== undefined);

    const spreadsheet = gridApi.getSheetDataForExcel({
      sheetName,
      columnKeys,
      prependContent: [
        {
          cells: [
            {
              data: {
                value: `${sheetName} Report ${moment(productionDate).format(
                  "MM-DD-YYYY",
                )}`,
                type: "String",
              },
              mergeAcross: 2,
            },
          ],
        },
      ],
      appendContent: [
        {
          cells: [
            {
              data: {
                value: `Generated on: ${new Date().toLocaleString()}`,
                type: "String",
              },
              mergeAcross: 2,
            },
          ],
        },
      ],
    });

    exportMultipleSheetsAsExcel({
      data: [spreadsheet].filter(
        (sheet): sheet is string => sheet !== undefined,
      ),
      fileName: `${sheetName
        .trim()
        .replace(/\s+/g, "_")
        .toLowerCase()}_report_${moment(productionDate).format("MM-DD-YYYY")}.xlsx`,
    });
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    throw error;
  }
};
